<template>
  <v-app>
    <TheNavbar v-if="showNavbar" />
    <TheHeader v-if="showHeader" />
    <v-main>
      <v-container :fluid="fluid" :class="{ 'fill-height': fillHeight }">
        <router-view />
      </v-container>
    </v-main>
    <TheFullScreenBtn v-if="showFullScreenBtn" />
    <TheFooter v-if="showFooter" />
    <TheSnackbar />
    <TheLoader />
  </v-app>
</template>

<script>
import { mapGetters } from "vuex";
import { computed, getCurrentInstance } from "vue";
import { useTitle } from "@vueuse/core";
import { useBrand } from "@/composables/useBrand";
import * as notificationsHub from "@/services/notificationsHub";
import TheNavbar from "@/components/global/TheNavbar.vue";
import TheHeader from "@/components/global/TheHeader.vue";
import TheSnackbar from "@/components/global/TheSnackbar.vue";
import TheFullScreenBtn from "./components/global/TheFullScreenBtn.vue";
import TheFooter from "./components/global/TheFooter.vue";
import TheLoader from "./components/global/TheLoader.vue";

const DBP_NAME = "Dynamic Business Planner";
const DSR_NAME = "Dynamic Sustainability Report";

export default {
  name: "App",
  components: {
    TheNavbar,
    TheHeader,
    TheSnackbar,
    TheFooter,
    TheFullScreenBtn,
    TheLoader,
  },
  setup() {
    const instance = getCurrentInstance();
    const { isDsr } = useBrand();
    const envName = process.env.VUE_APP_NAME || "";
    const prefixMatch = envName.match(/^(\[.*?\])\s/);
    const prefix = prefixMatch ? prefixMatch[1] + " " : "";
    const title = computed(() => {
      const route = instance.proxy.$route;
      const isAuthenticated = route.meta.navbar?.show;
      const baseName = (
        !isAuthenticated ? route.query.module === "dsr" : isDsr.value
      )
        ? DSR_NAME
        : DBP_NAME;
      return prefix + baseName;
    });
    useTitle(title);
  },
  computed: {
    ...mapGetters({
      isFullScreen: "shared/getFullScreen",
      userIsLogged: "user/userIsLogged",
    }),
    showNavbar() {
      return (!this.isFullScreen && this.$route.meta.navbar?.show) ?? false;
    },
    showHeader() {
      return (!this.isFullScreen && this.$route.meta.header?.show) ?? false;
    },
    showFooter() {
      return this.$route.meta.footer?.show ?? false;
    },
    showFullScreenBtn() {
      return this.$route.meta.fullScreen?.show ?? false;
    },
    fluid() {
      return (this.isFullScreen || this.$route.meta.container?.fluid) ?? false;
    },
    fillHeight() {
      return this.$route.meta.container?.fillHeight ?? false;
    },
  },
  watch: {
    // Avvia/ferma il canale notifiche real-time al login/logout
    // (immediate copre il refresh pagina con sessione persistita)
    userIsLogged: {
      immediate: true,
      handler(logged) {
        if (logged) {
          notificationsHub.start();
          this.$store.dispatch("notifications/fetchInitial").catch(() => {
            // Errore già notificato dall'interceptor axios
          });
        } else {
          notificationsHub.stop();
          this.$store.dispatch("notifications/resetState");
        }
      },
    },
  },
};
</script>

<style>
html {
  overflow: auto;
}

.v-application {
  background-color: #f0f0f0 !important;
}

/* Firefox (uncomment to work in Firefox, although other properties will not work!)  */
/** {
  scrollbar-width: thin;
  scrollbar-color: #050D21 #F0F0F0;
}*/

/* Chrome, Edge and Safari */
*::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}
*::-webkit-scrollbar-track {
  border-radius: 4px;
  background-color: #f0f0f0;
}

*::-webkit-scrollbar-track:hover {
  background-color: #d4d4d4;
}

*::-webkit-scrollbar-track:active {
  background-color: #d4d4d4;
}

*::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: #050d21;
}

*::-webkit-scrollbar-thumb:hover {
  background-color: #050d21;
}

*::-webkit-scrollbar-thumb:active {
  background-color: #050d21;
}
</style>
